import { Box, Select, Tooltip } from '@chakra-ui/react';
import { chakraComponents, Select as MultiSelect } from 'chakra-react-select';
import PropTypes from 'prop-types';
import { Children, forwardRef } from 'react';
import {
  chakraSelectValueForOptions,
  compactMultiSummary,
} from './selectInputChakraValue';

function CompactValueContainer(containerProps) {
  const summary = compactMultiSummary(
    containerProps.getValue(),
    containerProps.selectProps.compactMultiNoun
  );
  const isTyping = Boolean(containerProps.selectProps.inputValue);
  const showSummary = Boolean(summary) && !isTyping;
  const childArray = Children.toArray(containerProps.children);
  const input = childArray[childArray.length - 1];
  const values = childArray.slice(0, -1);

  return (
    <chakraComponents.ValueContainer {...containerProps}>
        {showSummary ? (
          <Box fontSize="sm" noOfLines={1} gridArea="1 / 1" minW={0}>
            {summary}
          </Box>
        ) : isTyping ? null : (
          values
        )}
      {input}
    </chakraComponents.ValueContainer>
  );
}

function CompactOption({ children, ...optionProps }) {
  return (
    <chakraComponents.Option {...optionProps}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        w="100%"
        gap={2}
      >
        <Box noOfLines={1}>{children}</Box>
        {optionProps.isSelected ? <Box aria-hidden="true">✓</Box> : null}
      </Box>
    </chakraComponents.Option>
  );
}

const SelectInput = forwardRef(
  (
    {
      options,
      value,
      isNumber,
      onChange,
      placeholder,
      isMulti,
      isDisabled,
      enableSearch,
      isComplexOptions,
      isClearable,
      showValueTooltip,
      compactMulti,
      compactMultiNoun,
      ...props
    },
    ref
  ) => {
    if (!options) {
      return [];
    }
    const createOptions = () => {
      return options.map((option) => {
        return (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        );
      });
    };

    const isChakraSelect = isMulti || enableSearch || isComplexOptions;

    const getValue = () => {
      if (isChakraSelect) {
        return chakraSelectValueForOptions(options, value, isMulti);
      }

      if (value === undefined || value === null) {
        return null;
      }

      return value;
    };

    const formatValue = (value) => {
      return isNumber ? Number(value) : value;
    };

    let attr = {
      ref,
      value: getValue(),
      isDisabled,
      placeholder: placeholder || '',
      fontSize: 'sm',
      color: 'secondaryGray.600',
      ...props,
    };

    if (isChakraSelect) {
      return (
        <MultiSelect
          {...attr}
          isMulti={isMulti}
          closeMenuOnSelect={
            attr.closeMenuOnSelect ?? (compactMulti && isMulti ? false : true)
          }
          hideSelectedOptions={
            attr.hideSelectedOptions ?? (compactMulti && isMulti ? false : true)
          }
          blurInputOnSelect={
            attr.blurInputOnSelect ?? (compactMulti && isMulti ? false : undefined)
          }
          options={options}
          useBasicStyles
          isClearable={isClearable}
          onChange={(selected) => {
            const value = isMulti
              ? selected.map((item) => formatValue(item.value))
              : formatValue(selected?.value);
            onChange(value);
          }}
          components={
            compactMulti
              ? {
                  ValueContainer: CompactValueContainer,
                  Option: CompactOption,
                }
              : undefined
          }
          compactMultiNoun={compactMultiNoun}
          chakraStyles={{
            ...(compactMulti
              ? {
                  control: (prev) => ({
                    ...prev,
                    minH: '44px',
                    h: '44px',
                  }),
                  valueContainer: (prev) => ({
                    ...prev,
                    display: 'grid',
                    alignItems: 'center',
                    flexWrap: 'nowrap',
                    overflow: 'hidden',
                    py: 0,
                    h: '44px',
                  }),
                  input: (prev, state) => ({
                    ...prev,
                    gridArea: '1 / 1',
                    minW: 0,
                    w: state.selectProps.inputValue ? '100%' : '1px',
                    opacity: state.selectProps.inputValue ? 1 : 0,
                  }),
                  indicatorsContainer: (prev) => ({
                    ...prev,
                    h: '44px',
                  }),
                  menu: (prev) => ({
                    ...prev,
                    minW: '240px',
                  }),
                }
              : {}),
            option: (prev, state) => ({
              ...prev,
              fontSize: 'sm',
            }),
            multiValueLabel: (prev, { selectProps }) => ({
              ...prev,
              fontWeight: props.fontWeight,
              p: 0,
            }),
          }}
        ></MultiSelect>
      );
    }

    return (
      <Tooltip
        label={
          showValueTooltip && value
            ? options.find((option) => option.value === value)?.label
            : ''
        }
        placement="top"
      >
        <Select
          {...attr}
          onChange={(event) => onChange(formatValue(event.target.value))}
          sx={{
            color:
              !attr.value && !!placeholder ? 'gray.400' : 'secondaryGray.900',
            fontWeight: !attr.value && !!placeholder ? 400 : props.fontWeight,
          }}
        >
          {createOptions()}
        </Select>
      </Tooltip>
    );
  }
);

SelectInput.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      value: PropTypes.any.isRequired,
    })
  ).isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
    PropTypes.array,
  ]),
  isNumber: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  variant: PropTypes.string,
  isMulti: PropTypes.bool,
  isDisabled: PropTypes.bool,
  enableSearch: PropTypes.bool,
  isComplexOptions: PropTypes.bool,
  isClearable: PropTypes.bool,
  showValueTooltip: PropTypes.bool,
  compactMulti: PropTypes.bool,
  compactMultiNoun: PropTypes.string,
};

export default SelectInput;
